import React, { useCallback, useEffect, useRef, useState } from 'react'

type VScrollAreaProps = {
  children: React.ReactNode
  /** Applied to the outer (positioned) wrapper. */
  className?: string
  /** Applied to the inner scrolling element. */
  contentClassName?: string
  ['data-testid']?: string
}

/**
 * Scroll container with a custom, always-visible vertical scrollbar (GEN-3300).
 *
 * Native scrollbars are overlay-style on macOS and stay hidden until you
 * actively scroll, so analysts couldn't tell panels scroll up/down. This
 * renders our own track + draggable thumb (mirroring the variant table's
 * horizontal bar, GEN-3298) and suppresses the native scrollbar to avoid a
 * double bar. The body is the source of truth for scroll position.
 */
const VScrollArea: React.FC<VScrollAreaProps> = ({ children, className, contentClassName, ...rest }) => {
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const trackRef = useRef<HTMLDivElement | null>(null)
  const [bar, setBar] = useState({ heightPct: 100, topPct: 0, scrollable: false })
  const dragRef = useRef<{ startY: number; startTop: number } | null>(null)

  const update = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    const { clientHeight: ch, scrollHeight: sh, scrollTop: st } = el
    if (sh <= ch + 1) {
      setBar({ heightPct: 100, topPct: 0, scrollable: false })
      return
    }
    setBar({ heightPct: (ch / sh) * 100, topPct: (st / sh) * 100, scrollable: true })
  }, [])

  // Recompute on mount, on container resize, and when the content height changes
  // (e.g. facets load, a different variant's detail renders).
  useEffect(() => {
    update()
    const el = scrollRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(update)
    ro.observe(el)
    const content = el.firstElementChild
    if (content) ro.observe(content)
    return () => ro.disconnect()
  }, [update])

  const onThumbPointerDown = useCallback((e: React.PointerEvent) => {
    const el = scrollRef.current
    if (!el) return
    e.preventDefault()
    e.stopPropagation()
    dragRef.current = { startY: e.clientY, startTop: el.scrollTop }
    const onMove = (ev: PointerEvent) => {
      const body = scrollRef.current
      const track = trackRef.current
      const drag = dragRef.current
      if (!body || !track || !drag) return
      const ratio = body.scrollHeight / (track.clientHeight || 1)
      body.scrollTop = drag.startTop + (ev.clientY - drag.startY) * ratio
    }
    const onUp = () => {
      dragRef.current = null
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }, [])

  const onTrackPointerDown = useCallback((e: React.PointerEvent) => {
    const el = scrollRef.current
    const track = trackRef.current
    if (!el || !track || e.target !== track) return
    const rect = track.getBoundingClientRect()
    const clickRatio = (e.clientY - rect.top) / (rect.height || 1)
    el.scrollTop = clickRatio * el.scrollHeight - el.clientHeight / 2
  }, [])

  return (
    <div className={`relative ${className ?? ''}`}>
      <div
        ref={scrollRef}
        onScroll={update}
        className={`no-native-scrollbar h-full overflow-auto ${contentClassName ?? ''}`}
        data-testid={rest['data-testid']}
      >
        {children}
      </div>
      <div
        ref={trackRef}
        onPointerDown={onTrackPointerDown}
        aria-hidden="true"
        className="absolute top-0 right-0.5 bottom-0 w-2.5 cursor-pointer rounded-full bg-slate-200/70"
        data-testid="vscroll-track"
      >
        <div
          onPointerDown={onThumbPointerDown}
          className={`absolute right-0 w-2.5 rounded-full transition-colors ${
            bar.scrollable ? 'cursor-grab bg-slate-400 hover:bg-slate-500 active:cursor-grabbing' : 'bg-slate-300'
          }`}
          style={{ height: `${bar.heightPct}%`, top: `${bar.topPct}%` }}
          data-testid="vscroll-thumb"
        />
      </div>
    </div>
  )
}

export default VScrollArea
